'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import type { ColDef, ICellRendererParams } from 'ag-grid-community';
import {
  DetectionMetric,
  GroupHeaderCell,
  type DetectionView,
} from '@terra/ui-design-system';
import { GRAPH_NODE_ICON } from '@terra/ui-graphs/external-network';
import { ListPageNameCell } from '../../../common/ListPageNameCell';
import { rowClickBlockerProps } from '../../rowClickBlocker';
import { AssetListCellSlot, renderAssetListValueCell } from '../AssetListCells';
import { AssetRowIcon } from '../AssetRowIcon';
import {
  ENET_ASSET_LIST_COLUMN,
  ENET_ASSET_LIST_COLUMNS,
  ENET_ASSET_LIST_COLUMN_WIDTHS,
  enetAssetListHasDetectionColumns,
  type EnetAssetListColumnId,
  type EnetAssetListKind,
} from '../asset-list-columns';
import { buildAssetDetectionMetricConfigs } from '../build-asset-detection-metric-configs';
import { getExternalNetworkEntityKindIcon } from '../surfaceAsset.constants';
import { useEnetAssetListColumnLabels } from '../useEnetAssetListColumnLabels';
import {
  isExternalAssetsGroupHeaderRow,
  isExternalAssetsSortColumn,
  type ExternalAssetGridItem,
  type ExternalAssetsTableRow,
} from './external-assets-table.model';

const NAME_COLUMN_MIN_WIDTH_PX = 224;
const VALUE_COLUMN_PADDING_PX = 24;

const preserveServerSortOrder = () => 0;

const valueColumnWidth = (column: EnetAssetListColumnId): number =>
  Number.parseInt(ENET_ASSET_LIST_COLUMN_WIDTHS[column], 10) +
  VALUE_COLUMN_PADDING_PX;

function ExternalAssetNameCell({ item }: { item: ExternalAssetGridItem }) {
  const Icon = getExternalNetworkEntityKindIcon(item.entityKind);

  return (
    <ListPageNameCell icon={<AssetRowIcon icon={Icon} />} title={item.label} />
  );
}

export function useExternalAssetsTableColumnDefs({
  listKind,
  detectionView,
  collapsedGroupKeys,
  onToggleGroup,
  onOpenAsset,
}: {
  listKind: EnetAssetListKind;
  detectionView: DetectionView;
  collapsedGroupKeys: ReadonlySet<string>;
  onToggleGroup: (groupKey: string) => void;
  onOpenAsset: (item: ExternalAssetGridItem) => void;
}): ColDef<ExternalAssetsTableRow>[] {
  const tCards = useTranslations('designSystem.applicationAssets.cards');
  const tKinds = useTranslations('externalNetwork.entityKinds');
  const tList = useTranslations('externalNetwork.assetList');
  const { nameColumnLabel, columnLabel } = useEnetAssetListColumnLabels();

  return useMemo(() => {
    const valueColumns = ENET_ASSET_LIST_COLUMNS[listKind];
    const includeDetectionColumns = enetAssetListHasDetectionColumns({
      listKind,
    });

    const renderDetectionCell = ({
      item,
      key,
    }: {
      item: ExternalAssetGridItem;
      key: 'findings' | 'signals';
    }) => {
      const metric = buildAssetDetectionMetricConfigs({
        metrics: item.metrics,
        detectionView,
        includeDetectionColumns,
        findingsLabel: tCards('findings'),
        activeSignalsLabel: tCards('signals', {
          count: item.metrics?.activeSignalsCount ?? 0,
        }),
        totalSignalsLabel: tCards('signals', {
          count: item.metrics?.signalsCount ?? 0,
        }),
      }).find((config) => config.key === key);

      return metric ? (
        <AssetListCellSlot>
          <DetectionMetric
            metric={metric}
            detectionView={detectionView}
            endpointId={item.id}
            disableHover
          />
        </AssetListCellSlot>
      ) : null;
    };

    const nameColumn: ColDef<ExternalAssetsTableRow> = {
      colId: 'name',
      headerName: nameColumnLabel(listKind),
      flex: 1,
      minWidth: NAME_COLUMN_MIN_WIDTH_PX,
      lockPosition: 'left',
      sortable: true,
      comparator: preserveServerSortOrder,
      colSpan: (params) =>
        isExternalAssetsGroupHeaderRow(params.data)
          ? valueColumns.length + 1
          : 1,
      cellRenderer: (params: ICellRendererParams<ExternalAssetsTableRow>) => {
        if (isExternalAssetsGroupHeaderRow(params.data)) {
          const GroupIcon = params.data.entityKind
            ? getExternalNetworkEntityKindIcon(params.data.entityKind)
            : null;

          return (
            <GroupHeaderCell
              group={params.data}
              isCollapsed={collapsedGroupKeys.has(params.data.groupKey)}
              onToggle={onToggleGroup}
              containerProps={rowClickBlockerProps}
              icon={GroupIcon ? <GroupIcon size={16} /> : undefined}
            />
          );
        }

        return params.data ? (
          <ExternalAssetNameCell item={params.data} />
        ) : null;
      },
    };

    const dataColumns = valueColumns.map(
      (column): ColDef<ExternalAssetsTableRow> => ({
        colId: column,
        headerName: columnLabel(column),
        width: valueColumnWidth(column),
        minWidth: valueColumnWidth(column),
        sortable: isExternalAssetsSortColumn(column),
        comparator: preserveServerSortOrder,
        headerClass: 'ag-center-aligned-header',
        cellRenderer: (params: ICellRendererParams<ExternalAssetsTableRow>) => {
          const item = params.data;

          if (!item || isExternalAssetsGroupHeaderRow(item)) {
            return null;
          }

          if (
            column === ENET_ASSET_LIST_COLUMN.findings ||
            column === ENET_ASSET_LIST_COLUMN.signals
          ) {
            return renderDetectionCell({ item, key: column });
          }

          return (
            <AssetListCellSlot>
              {renderAssetListValueCell({
                column,
                values: item.cells,
                countIcons: {
                  hosts: GRAPH_NODE_ICON.HOST,
                  subdomains: GRAPH_NODE_ICON.DOMAIN,
                  domains: GRAPH_NODE_ICON.DOMAIN,
                  services: GRAPH_NODE_ICON.NETWORK_SERVICE,
                },
                countLabels: {
                  hosts: tKinds('host'),
                  subdomains: tKinds('domain'),
                  domains: tKinds('domain'),
                  services: tKinds('service'),
                },
                inheritedLabel: tList('inherited'),
                onCountSelect: () => onOpenAsset(item),
              })}
            </AssetListCellSlot>
          );
        },
      }),
    );

    return [nameColumn, ...dataColumns];
  }, [
    collapsedGroupKeys,
    columnLabel,
    detectionView,
    listKind,
    nameColumnLabel,
    onOpenAsset,
    onToggleGroup,
    tCards,
    tKinds,
    tList,
  ]);
}
